import {
  Box,
} from '@mui/material'
import { useState, useMemo } from 'react'
import { useAppTheme } from '@/theme/ThemeContext'
import DataGrid from '@/components/Table/DataGrid'
import generalColumns from '@/components/features/config/columns/consumers/general-2.columns'
import { userRows } from '@/mockdata/usersData'
import FilterToolbar, { type FilterToolbarField } from '@/components/common/FilterToolbar'
import { getDefaultFifteenthDateRange } from '@/utils/consumerReadingsDateRanges'

export type GeneralFilters = {
  from: Date | null
  to: Date | null
  eventType: 'all' | 'התראה' | 'החלפה' | 'crm'
}

const columns = generalColumns

const parseDMY = (d: string) => {
  const [dd, mm, yyyy] = d.split('/').map(Number)
  return new Date(yyyy, mm - 1, dd)
}
const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0)
const endOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999)

const General = () => {
  const { mode } = useAppTheme()
  const isDarkMode = mode === 'dark'
  const { from: defaultFrom, to: defaultTo } = useMemo(() => getDefaultFifteenthDateRange(), [])

  const adjustedUserRows = useMemo(() => {
    const currentYear = new Date().getFullYear()
    return userRows.map((row: any) => {
      if (row.date) {
        const parts = row.date.split('/')
        if (parts.length === 3) {
          return {
            ...row,
            date: `${parts[0]}/${parts[1]}/${currentYear}`
          }
        }
      }
      return row
    })
  }, [])

  const [filters, setFilters] = useState<GeneralFilters>({
    from: defaultFrom,
    to: defaultTo,
    eventType: 'all',
  })

  const [filteredRows, setFilteredRows] = useState(() => {
    return adjustedUserRows.filter(row => {
      const rowDate = parseDMY(row.date)
      const okFrom = rowDate >= startOfDay(defaultFrom)
      const okTo = rowDate <= endOfDay(defaultTo)
      return okFrom && okTo
    })
  })
  const [showClear, setShowClear] = useState(false)

  const handleSearch = () => {
    const { from, to, eventType } = filters

    const next = adjustedUserRows.filter(row => {
      const rowDate = parseDMY(row.date)
      const okFrom = !from || rowDate >= startOfDay(from)
      const okTo = !to || rowDate <= endOfDay(to)

      const typeOk = eventType === 'all' || (row.activity ?? '').toLowerCase() === eventType.toLowerCase()

      return okFrom && okTo && typeOk
    })

    setFilteredRows(next)
  }

  const clearFilters = () => {
    setFilters({ from: null, to: null, eventType: 'all' })
    setFilteredRows(adjustedUserRows)
    setShowClear(false)
  }

  const filterFields: FilterToolbarField[] = [
    {
      name: 'eventType',
      type: 'select',
      label: 'סוג אירוע',
      value: filters.eventType,
      options: [
        { value: 'all', label: 'הכל' },
        { value: 'התראה', label: 'התראה' },
        { value: 'החלפה', label: 'החלפה' },
        { value: 'crm', label: 'CRM' },
      ],
      onChange: value => {
        setFilters(previous => ({ ...previous, eventType: value as GeneralFilters['eventType'] }))
        setShowClear(value !== 'all')
      },
    },
    {
      name: 'from',
      type: 'date',
      label: 'מתאריך',
      value: filters.from,
      onChange: value => setFilters(previous => ({ ...previous, from: value })),
    },
    {
      name: 'dateSeparator',
      type: 'custom',
      render: <Box sx={{ width: 10, height: 2, bgcolor: isDarkMode ? '#BBD7FF' : '#003F61' }} />,
    },
    {
      name: 'to',
      type: 'date',
      label: 'עד תאריך',
      value: filters.to,
      onChange: value => setFilters(previous => ({ ...previous, to: value })),
    },
  ]

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          padding: 2,
          borderBottom: `1px solid ${isDarkMode ? '#374151' : '#E6EEF5'}`,
          width: '100%',
          display: 'flex',
          justifyContent: 'flex-end',
        }}
      >
        <Box sx={{ width: { xs: '100%', md: '60%' } }}>
          <FilterToolbar
            fields={filterFields}
            onSearch={handleSearch}
            onClear={showClear ? clearFilters : undefined}
            clearLabel="נקה"
          />
        </Box>
      </Box>

      <Box
        sx={{
          padding: 3,
          display: 'flex',
          justifyContent: 'flex-end',
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: { xs: '100%', md: '60%' },
            '& .MuiDataGrid-root': { border: 'none' },
          }}
        >
          <DataGrid
            columns={columns}
            rows={filteredRows}
            hideFooter
            pageSize={25}
            checkboxSelection={false}
            width="100%"
            headerPosition="bottom"
            maxHeight='calc(100vh - 330px)'
          />
        </Box>
      </Box>

    </Box>
  )
}

export default General
